/****************************************************
 * Module Name:  AffectedStonesRapnet.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Button, Tooltip, styled, MenuItem, Menu, alpha, Typography, Stack } from "@mui/material";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import StoneDetailModal from "../common/stoneDetailModal";
import renderCellExpand from "../common/dataGridCellExpand";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import colourchart from '../../images/ColorScheme.png'
import RapnetUploadProxy from "../../utility/proxy/manager/rapnetUploadProxy";
import Select from "react-select";
import NoPermission from "../common/noPermission";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#B6B9D6",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#B6B9D6",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_OUT": {
        backgroundColor: "#CAF7F6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CAF7F6",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

export default function AffectedStonesRapnet() {

    const [stoneId, setStoneId] = useState();
    const [stoneData, setStoneData] = useState([]);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarVariant, setSnackbarVariant] = useState();
    const [loader, setLoader] = useState(false);
    const [filterModel, setFilterModel] = useState();
    const [columnNames, setColumnNames] = useState([]);
    const [noOfSelectedStones, setNoOfSelectedStones] = useState();
    const [selectedTotalSize, setSelectedTotalSize] = useState();
    const [selectedtotalPrice, setSelectedtotalPrice] = useState();
    const [selectedavgPrice, setSelectedavgPrice] = useState();
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState();
    const [stonetypeOptions, setStonetypeOptions] = useState([]);
    const [stonetype, setStonetype] = useState([]);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_RAPNET_AFFECTED_STONE);

    useEffect(() => {
        PageTitle("OPERATION_RAPNET_AFFECTED_STONE");
        let stonetypeOptions = [];
        stonetypeOptions.push({ value: -1, label: " -- ANY -- " });
        Constants.uniqueFilterValues.stonetype.forEach(iStonetype => {
            stonetypeOptions.push({ value: iStonetype.name, label: iStonetype.fullname });
        })
        setStonetypeOptions(stonetypeOptions);
        setStonetype(stonetypeOptions.filter(iStonetype => iStonetype.value === 'N'));
    }, []);

    useEffect(() => {
        if (stonetype.length > 0) {
            handleSearch();
        }
    }, [stonetype]);

    const handleSearch = () => {
        let lstStonetype = [];
        let bStonetype = stonetype.find(iStonetype => iStonetype.value === -1);
        if (!bStonetype) {
            lstStonetype = stonetype.map(iStonetype => iStonetype.value);
        }

        setLoader(true);
        RapnetUploadProxy.GetRapnetAffectedStones(lstStonetype, (response) => {
            if (response) {
                setLoader(false);
                const uniqueFilterValues = {
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    status: Utility.getDistinctOptions(response, "status"),
                    location: Utility.getDistinctOptions(response, "location"),
                    lab: Utility.getDistinctOptions(response, "lab"),
                    shade: Utility.getDistinctOptions(response, "shade"),
                    country: Utility.getDistinctOptions(response, "country"),
                };
                setStoneData(response);
                loadTotalData(response);
                prepareColumns(uniqueFilterValues);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    // Summary
    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    const prepareColumns = (uniqueFilterValues) => {
        let columnNames = [];
        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "rapnetprice") {
                    columnNames.push({
                        field: "prevrapnetdate",
                        headerName: "PREV RAP DATE",
                        headerAlign: "center",
                        align: "center",
                        width: 115,
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => params.value
                    });
                    columnNames.push({
                        field: "prevrapnetprice",
                        headerName: "PREV RAP",
                        headerAlign: "center",
                        align: "right",
                        width: 90,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.prevrapnetprice),
                        cellClassName: 'lavenderCell'
                    });
                    columnNames.push({
                        field: "prevdiscount",
                        headerName: "PREV DIS",
                        headerAlign: "center",
                        align: "center",
                        width: 90,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.prevdiscount)
                    });
                    columnNames.push({
                        field: "prevprice",
                        headerName: "PREV PPC",
                        headerAlign: "center",
                        align: "right",
                        width: 90,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.prevppc)
                    });
                    columnNames.push({
                        field: "prevtotal",
                        headerName: "PREV TOTAL",
                        headerAlign: "center",
                        align: "right",
                        width: 90,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.prevppc * params.row.size)
                    });
                    columnNames.push({
                        field: "rapnetdate",
                        headerName: "RAP DATE",
                        headerAlign: "center",
                        align: "center",
                        width: 90,
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => params.value
                    });
                }

                if (uniqueFilterValues[columnData.field]) {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues[columnData.field];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);

                if (columnData.field === "rapnetprice") {
                    columnNames.push({
                        field: "caratprice",
                        headerName: "UPDATE RAP PENDING",
                        headerAlign: "center",
                        align: "center",
                        width: 165,
                        type: "number",
                        valueGetter: (params) => params.row.rapnetprice === params.row.caratprice ? 0 : params.row.caratprice,
                        renderCell: (params) => params.row.rapnetprice === params.row.caratprice ? "-" :
                            Utility.ConvertNumberWithSpecifiedDigits(params.row.caratprice),
                        cellClassName: (params) => params.row.rapnetprice === params.row.caratprice ? "" :
                            "yellowCell"
                    });
                }
            }
        });
        setColumnNames(columnNames);
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STONE STATUS",
            headerAlign: "center",
            align: "center",
            width: 110,
            valueGetter: (params) => (params.value),
            renderCell: (params) => (params.value),
            cellClassName: (params) => `super-app-theme--${params.row.status.toUpperCase()}`
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand,
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION ID",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice),
            cellClassName: (params) => params.row.rapnetprice > params.row.prevrapnetprice ? 'greenCell' : params.row.rapnetprice === params.row.prevrapnetprice ? null : 'redCell'
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            minWidth: 50,
            renderCell: renderCellExpand,
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.colortype.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        }
    };

    const rows = stoneData;
    const columns = columnNames;

    // Color Scheme Drop Down Button
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseButton = () => {
        setAnchorEl(null);
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {loader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => stonetype.length === 0 ? "" : handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                            disabled={true}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return (
        !Utility.isAddAllowed(local_Permission) ? <NoPermission /> : (
            <Box>
                <TabMenuSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" : (snackbarVariant === "error" ? "Failed" : "Warning")}
                    handleClose={() => setShowSnackbar(false)}
                />

                {showStoneDetailModal ? (
                    <StoneDetailModal
                        show={showStoneDetailModal}
                        stoneId={stoneId}
                        handleClose={() => setShowStoneDetailModal(false)}
                    />
                ) : null}
                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanId}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}
                {showPacketDetailModal ? (
                    <PacketHistoryModal
                        show={showPacketDetailModal}
                        packetId={packetId}
                        kapanId={kapanId}
                        close={() => setShowPacketDetailModal(false)}
                    />
                ) : null}
                <Box display="flex" flexWrap="wrap" gap="15px" margin="4px 8px" alignItems="center">
                    {/* Label + Select Combo */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            gap: "8px",
                            flexGrow: 1,
                            minWidth: "250px",
                        }}
                    >
                        <Typography>Stonetype</Typography>
                        <Box sx={{ minWidth: "200px", width: "23rem", }}>
                            <Select
                                menuPlacement="auto"
                                blurInputOnSelect={false}
                                menuPortalTarget={document.body}
                                styles={{
                                    ...Constants.Styles,
                                    control: (base) => ({
                                        ...base,
                                        width: "100%",
                                    }),
                                }}
                                closeMenuOnSelect={false}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                isMulti
                                options={stonetypeOptions}
                                value={stonetype}
                                onChange={(e) => setStonetype(e)}
                            />
                        </Box>
                    </Box>

                    {/* Stats Section */}
                    <Box
                        sx={{
                            flexGrow: 2,
                            flexBasis: 0,
                            overflow: "hidden",
                            color: "black",
                            fontWeight: "normal",
                            textAlign: "center",
                            paddingTop: "21px",
                            paddingBottom: "21px",
                            minWidth: "250px",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                                No of Stones: <b>{noOfSelectedStones}</b>
                            </Typography>
                            <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                                Total Weight: <b>{selectedTotalSize}</b>
                            </Typography>
                            <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                                Total Price: <b>{selectedtotalPrice}</b>
                            </Typography>
                            <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                                Avg Price: <b>{selectedavgPrice}</b>
                            </Typography>
                            <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                                Avg Discount: <b>{selectedAvgDiscount}</b>
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Color Palette */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "15px",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Box>
                            <Box
                                id="demo-customized-button"
                                aria-controls={open ? 'demo-customized-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                                sx={{
                                    color: "#454545",
                                    borderColor: "#454545",
                                    borderRadius: "4px",
                                    border: "1px solid black",
                                    padding: "5px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "4rem",
                                }}
                            >
                                <img
                                    src={colourchart}
                                    alt="Color Scheme"
                                    style={{ height: '24px', width: '24px' }}
                                />
                                <KeyboardArrowDownIcon />
                            </Box>

                            <StyledMenu
                                id="demo-customized-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'demo-customized-button',
                                    style: { maxHeight: 250 },
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleCloseButton}
                            >
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#000", borderRadius: 20, margin: 10 }}></span>Available
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#b6b9d6", borderRadius: 20, margin: 10 }}></span>Hold
                                </MenuItem>
                                <MenuItem onClick={handleCloseButton} disableRipple>
                                    <span style={{ height: "15px", width: "15px", backgroundColor: "#CAF7F6", borderRadius: 20, margin: 10 }}></span>Memo Out
                                </MenuItem>
                            </StyledMenu>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        marginTop: "5px",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        "& .redCell": {
                            color: "#080808",
                            backgroundColor: "#f5d7d7",
                        },
                        "& .greenCell": {
                            color: "#080808",
                            backgroundColor: "#B6D7A8"
                        },
                        "& .yellowCell": {
                            color: '#080808',
                            backgroundColor: '#F1E68C',
                        },
                        "& .cursorNA": {
                            cursor: "not-allowed"
                        },
                        height: '70vh'
                    }}
                >
                    <StyledDataGrid
                        disableRowSelectionOnClick={true}
                        localeText={{ noRowsLabel: "No Record Found" }}
                        slots={{
                            toolbar: CustomToolbar,
                        }}
                        rows={rows}
                        density="compact"
                        columns={columns}
                        getRowId={(row) => row.stoneid}
                        loading={loader}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    locationid: false,
                                    kapanid: false,
                                    packetid: false,
                                    prevrapnetdate: false,
                                    rapnetdate: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        filterModel={filterModel}
                        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500,
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        )
    )
}